import { IResort } from '../../features/resort/types'
import { buildImageUrl } from './imageUrlUtils'
import { toTitleCase } from './formattingUtils'

// Simple mapping for US states; expand as needed
const STATE_ABBREVIATIONS: Record<string, string> = {
  ALABAMA: 'AL',
  ALASKA: 'AK',
  ARIZONA: 'AZ',
  ARKANSAS: 'AR',
  CALIFORNIA: 'CA',
  COLORADO: 'CO',
  CONNECTICUT: 'CT',
  DELAWARE: 'DE',
  FLORIDA: 'FL',
  GEORGIA: 'GA',
  HAWAII: 'HI',
  IDAHO: 'ID',
  ILLINOIS: 'IL',
  INDIANA: 'IN',
  IOWA: 'IA',
  KANSAS: 'KS',
  KENTUCKY: 'KY',
  LOUISIANA: 'LA',
  MAINE: 'ME',
  MARYLAND: 'MD',
  MASSACHUSETTS: 'MA',
  MICHIGAN: 'MI',
  MINNESOTA: 'MN',
  MISSISSIPPI: 'MS',
  MISSOURI: 'MO',
  MONTANA: 'MT',
  NEBRASKA: 'NE',
  NEVADA: 'NV',
  'NEW HAMPSHIRE': 'NH',
  'NEW JERSEY': 'NJ',
  'NEW MEXICO': 'NM',
  'NEW YORK': 'NY',
  'NORTH CAROLINA': 'NC',
  'NORTH DAKOTA': 'ND',
  OHIO: 'OH',
  OKLAHOMA: 'OK',
  OREGON: 'OR',
  PENNSYLVANIA: 'PA',
  'RHODE ISLAND': 'RI',
  'SOUTH CAROLINA': 'SC',
  'SOUTH DAKOTA': 'SD',
  TENNESSEE: 'TN',
  TEXAS: 'TX',
  UTAH: 'UT',
  VERMONT: 'VT',
  VIRGINIA: 'VA',
  WASHINGTON: 'WA',
  'WEST VIRGINIA': 'WV',
  WISCONSIN: 'WI',
  WYOMING: 'WY',
  // Add more as needed
}

function toStateAbbreviation(stateFull: string): string {
  if (!stateFull) return ''
  return STATE_ABBREVIATIONS[stateFull.trim().toUpperCase()] || stateFull
}

function prependAEMUrlIfRelative(path: string): string {
  if (!path) return ''
  // Use the buildImageUrl utility but handle the leading slash removal
  const cleanPath = path.replace(/^\//, '')
  return buildImageUrl(cleanPath)
}

export function mapResortToIResort(r: any): IResort {
  return {
    accessibility: r.accessibility || [],
    activities: (r.images?.icons?.activities || []).map((a: any) => ({ label: a.label, icon: a.icon })),
    address: {
      line1: r.address,
      line2: r.address2 || '',
      line3: r.address3 || '',
      country: r.country || '',
      countryFull: r.countryFull || '',
      city: toTitleCase(r.city || r.address?.city || ''),
      state: r.state || r.address?.state || toStateAbbreviation(r.stateFull || ''),
      stateFull: r.stateFull || '',
      postalCode: r.zipcode || '',
    },
    alerts: r.alerts || [],
    amenities: (r.images?.icons?.amenities || []).map((a: any) => ({ label: a.label, icon: a.icon })),
    bannerImage: r.bannerImage || '',
    canonicalUrl: r.canonicalUrl || '',
    state: r.state || toStateAbbreviation(r.stateFull || ''),
    stateFull: r.stateFull || '',
    description: r.description || '',
    fees: r.fees || '',
    images: r.images || { gallery: { amenities: [] }, suiteTypes: [] },
    latitude: r.latitude || '',
    locationCity: r.locationCity || { name: '', id: 0, state: { name: '', id: 0, region: { country: { name: '', location: { name: '', id: 0 }, id: 0 }, name: '', id: 0 } } },
    longitude: r.longitude || '',
    metaDescription: r.metaDescription || '',
    minPriceDisplay: r.minPriceDisplay || '',
    name: r.prodName || r.name || '',
    phone: r.phone || '',
    primaryImage: prependAEMUrlIfRelative(r.primaryImage || ''),
    productHubId: r.productHubId || '',
    resortFeesShort: r.resortFeesShort || '',
    clubPassFeesShort: r.clubPassFeesShort || '',
    resortInfo: r.resortInformation || r.resortInfo || [],
    resortNumber: r.resortNumber || '',
    resortType: r.resortType || '',
    resourceKey: r.resourceKey,
    reviews: r.reviews || {
      ai: '',
      averageRating: r.averageRating || 0,
      list: [],
      metrics: { stars5: 0, stars4: 0, stars3: 0, stars2: 0, stars1: 0 },
      numberOfReviews: r.numberOfReviews || 0,
    },
    schema: r.schema || {
      '@context': '',
      '@type': '',
      name: '',
      url: '',
      address: {
        '@type': '',
        streetAddress: '',
        addressLocality: '',
        addressRegion: '',
        postalCode: '',
        addressCountry: '',
      },
      telephone: '',
      aggregateRating: {
        '@type': '',
        ratingValue: 0,
        reviewCount: 0,
      },
    },
    similarProperties: r.similarProperties || [],
    suites: r.suites || r.roomTypes || [],
    surroundingArea: r.surroundingArea || { attractions: [], gettingAround: [], neighborhood: '', restaurants: [] },
    vacationExperiences: r.vacationExperiences || '',
    vacationVibes: r.vacationVibes || [],
    yextId: r.yextId || '',
    media: r.media,
    // First available dates for flexible date searches
    firstAvailableCheckin: r.firstAvailableCheckin,
    firstAvailableCheckout: r.firstAvailableCheckout,
    firstAvailableDisplayText: r.firstAvailableDisplayText,
    // Available suite count from backend
    availableSuiteCount: r.availableSuiteCount,
    clubPassResort: r.clubPassResort,
  }
}
